import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, RefreshCw, Download, TrendingUp, TrendingDown, Minus, Info, Receipt } from "lucide-react";
import { ClientTitlesDialog } from "@/components/ClientTitlesDialog";

const BANDS = {
  A: { label: "Excelente", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", bar: "bg-emerald-500", range: "800–1000" },
  B: { label: "Bom", cls: "bg-lime-500/15 text-lime-300 border-lime-500/30", bar: "bg-lime-500", range: "650–799" },
  C: { label: "Regular", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30", bar: "bg-amber-500", range: "500–649" },
  D: { label: "Risco", cls: "bg-orange-500/15 text-orange-300 border-orange-500/30", bar: "bg-orange-500", range: "350–499" },
  E: { label: "Crítico", cls: "bg-red-500/15 text-red-300 border-red-500/30", bar: "bg-red-500", range: "0–349" },
};

const SITUACAO = {
  adiantado: "Pago adiantado",
  no_dia: "Pago no vencimento",
  proximo_dia_util: "Pago no próximo dia útil (venceu em fim de semana/feriado) — sem atraso",
  tolerancia: "Pago dentro da tolerância",
  atrasado: "Pago com atraso",
  aberto_vencido: "Em aberto e vencido",
};

const VIEWS = [
  { id: "all", label: "Todos" },
  { id: "best", label: "Melhores pagadores" },
  { id: "worst", label: "Mais atrasam" },
  { id: "open", label: "Com atraso em aberto" },
  { id: "down", label: "Piorando" },
  { id: "up", label: "Melhorando" },
  { id: "new", label: "Novos (pouco histórico)" },
];

const SORTS = [
  { id: "score_desc", label: "Maior score" },
  { id: "score_asc", label: "Menor score" },
  { id: "pont_desc", label: "Maior pontualidade" },
  { id: "pont_asc", label: "Menor pontualidade" },
  { id: "late_desc", label: "Maior atraso médio" },
  { id: "name", label: "Nome (A-Z)" },
];

const fmtDate = (s) => (s ? s.split("-").reverse().join("/") : "—");

function ScoreBadge({ row }) {
  const b = BANDS[row.band];
  return (
    <div className="flex items-center gap-2 min-w-[130px]">
      <span className="font-outfit font-bold text-white w-10 text-right tabular-nums">{row.score}</span>
      <div className="h-2 flex-1 rounded-full bg-slate-800 overflow-hidden">
        <div className={`h-full ${b.bar}`} style={{ width: `${Math.max(2, row.score / 10)}%` }} />
      </div>
    </div>
  );
}

function BandPill({ band }) {
  const b = BANDS[band];
  return <span className={`inline-block px-2 py-0.5 rounded-md text-xs border ${b.cls}`}>{band} · {b.label}</span>;
}

function Trend({ t }) {
  if (t === "melhorando") return <span className="inline-flex items-center gap-1 text-emerald-400 text-xs"><TrendingUp size={14} />Melhorando</span>;
  if (t === "piorando") return <span className="inline-flex items-center gap-1 text-red-400 text-xs"><TrendingDown size={14} />Piorando</span>;
  if (t === "novo") return <span className="text-slate-500 text-xs">Novo</span>;
  return <span className="inline-flex items-center gap-1 text-slate-400 text-xs"><Minus size={14} />Estável</span>;
}

function Kpi({ label, value, hint, testid }) {
  return (
    <Card className="bg-slate-900/50 border-slate-800 p-4" data-testid={testid}>
      <div className="text-slate-500 text-xs uppercase tracking-[0.12em]">{label}</div>
      <div className="font-outfit text-2xl font-bold text-white mt-1">{value}</div>
      {hint && <div className="text-slate-500 text-xs mt-1">{hint}</div>}
    </Card>
  );
}

function DetailDialog({ login, params, onClose, onTitles }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!login) return;
    setD(null);
    api.get(`/score/${encodeURIComponent(login)}`, { params })
      .then((r) => setD(r.data))
      .catch((e) => { toast.error(formatApiError(e)); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login]);

  const comps = d?.componentes || {};
  const compRows = [
    ["Pontualidade (40%)", comps.pontualidade, "Quantos boletos foram pagos sem atraso, dando mais peso aos recentes"],
    ["Gravidade dos atrasos (25%)", comps.gravidade, "Atrasos curtos pesam menos que atrasos longos"],
    ["Últimos 90 dias (20%)", comps.recente, "Como o cliente vem pagando agora"],
    ["Sequência em dia (15%)", comps.sequencia, "Boletos seguidos pagos em dia (máximo 12)"],
  ];

  return (
    <Dialog open={!!login} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="score-detail-dialog" className="bg-slate-950 border-slate-800 text-slate-200 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white font-outfit">{d ? d.nome : "Carregando…"}</DialogTitle>
          <DialogDescription className="text-slate-500">
            {d ? `${d.login}${d.plano ? ` · ${d.plano}` : ""}${d.cliente_desde ? ` · cliente desde ${fmtDate(d.cliente_desde)}` : ""}` : ""}
          </DialogDescription>
        </DialogHeader>
        {d && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-4">
              <div className="font-outfit text-5xl font-bold text-white tabular-nums">{d.score}</div>
              <div className="space-y-1">
                <BandPill band={d.band} />
                <div className="text-sm text-slate-300">{d.perfil}</div>
                <Trend t={d.tendencia} />
              </div>
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" className="border-slate-700 text-slate-200" onClick={() => onTitles(d.login)}>
                  <Receipt size={14} className="mr-1" />Boletos / cobrar
                </Button>
              </div>
            </div>

            {d.novo && (
              <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-md p-2">
                Pouco histórico no período ({d.titulos} boleto{d.titulos === 1 ? "" : "s"}): o score ainda é uma estimativa e tende ao valor neutro.
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              {[
                ["Boletos avaliados", d.titulos],
                ["Pontualidade", d.pontualidade == null ? "—" : `${d.pontualidade}%`],
                ["Atraso médio", `${d.atraso_medio} dia(s)`],
                ["Maior atraso", `${d.maior_atraso} dia(s)`],
                ["Pagos em dia", d.em_dia],
                ["Pagos com atraso", d.atrasados],
                ["Em aberto vencidos", d.abertos_vencidos],
                ["Sequência em dia", d.sequencia_em_dia],
                ["Adiantamento médio", `${d.adiantamento_medio} dia(s)`],
                ["Pagou no próx. dia útil", d.pagou_proximo_dia_util],
                ["Desconto por atraso aberto", d.penalidade ? `−${d.penalidade} pts` : "—"],
                ["Bloqueado agora", d.bloqueado ? "Sim" : "Não"],
              ].map(([k, v]) => (
                <div key={k} className="bg-slate-900/60 border border-slate-800 rounded-md p-2">
                  <div className="text-slate-500 text-xs">{k}</div>
                  <div className="text-white font-medium">{v}</div>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <div className="text-xs uppercase tracking-[0.12em] text-slate-500">Como chegou nesse score</div>
              {compRows.map(([k, v, tip]) => (
                <div key={k} title={tip}>
                  <div className="flex justify-between text-xs text-slate-400"><span>{k}</span><span>{v ?? 0}%</span></div>
                  <div className="h-2 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${v ?? 0}%` }} /></div>
                </div>
              ))}
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.12em] text-slate-500 mb-2">Histórico de boletos</div>
              <div className="border border-slate-800 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900 text-slate-400 text-xs">
                    <tr><th className="text-left p-2">Vencimento</th><th className="text-left p-2">Pago em</th><th className="text-left p-2">Situação</th><th className="text-right p-2">Atraso</th></tr>
                  </thead>
                  <tbody>
                    {d.history.map((h, i) => (
                      <tr key={h.id || i} className="border-t border-slate-800">
                        <td className="p-2">
                          {fmtDate(h.vencimento)}
                          {h.regra_dia_util && <span className="block text-[11px] text-slate-500">vale para {fmtDate(h.vencimento_efetivo)} (dia útil)</span>}
                        </td>
                        <td className="p-2">{fmtDate(h.pago_em)}</td>
                        <td className={`p-2 ${h.situacao === "atrasado" || h.situacao === "aberto_vencido" ? "text-red-300" : "text-slate-300"}`}>{SITUACAO[h.situacao]}</td>
                        <td className="p-2 text-right tabular-nums">{h.atraso > 0 ? `${h.atraso} d` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Score() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);
  const [tolerance, setTolerance] = useState(0);
  const [search, setSearch] = useState("");
  const [band, setBand] = useState("");
  const [view, setView] = useState("all");
  const [sort, setSort] = useState("score_desc");
  const [detail, setDetail] = useState(null);
  const [titlesLogin, setTitlesLogin] = useState(null);
  const [showHow, setShowHow] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE = 25;

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/score", { params: { months, tolerance, refresh } });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [months, tolerance]);
  useEffect(() => { setPage(1); }, [search, band, view, sort, months, tolerance]);

  const rows = useMemo(() => {
    if (!data) return [];
    let r = data.rows;
    const q = search.trim().toLowerCase();
    if (q) r = r.filter((x) => `${x.nome} ${x.login} ${x.celular}`.toLowerCase().includes(q));
    if (band) r = r.filter((x) => x.band === band);
    if (view === "best") r = r.filter((x) => !x.novo && x.band === "A");
    if (view === "worst") r = r.filter((x) => !x.novo && (x.band === "D" || x.band === "E"));
    if (view === "open") r = r.filter((x) => x.abertos_vencidos > 0);
    if (view === "down") r = r.filter((x) => x.tendencia === "piorando");
    if (view === "up") r = r.filter((x) => x.tendencia === "melhorando");
    if (view === "new") r = r.filter((x) => x.novo);
    const s = [...r];
    const by = {
      score_desc: (a, b) => b.score - a.score,
      score_asc: (a, b) => a.score - b.score,
      pont_desc: (a, b) => (b.pontualidade ?? -1) - (a.pontualidade ?? -1) || b.score - a.score,
      pont_asc: (a, b) => (a.pontualidade ?? 101) - (b.pontualidade ?? 101) || a.score - b.score,
      late_desc: (a, b) => b.atraso_medio - a.atraso_medio,
      name: (a, b) => a.nome.localeCompare(b.nome),
    }[sort];
    s.sort(by);
    return s;
  }, [data, search, band, view, sort]);

  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const slice = rows.slice((page - 1) * PAGE, page * PAGE);
  const sm = data?.summary;

  const exportCsv = () => {
    const head = ["Login", "Nome", "Plano", "Score", "Faixa", "Perfil", "Tendencia", "Boletos", "Pontualidade %", "Atraso medio (d)", "Maior atraso (d)", "Abertos vencidos", "Bloqueado"];
    const lines = rows.map((r) => [r.login, r.nome, r.plano, r.score, r.band, r.perfil, r.tendencia, r.titulos,
      r.pontualidade ?? "", r.atraso_medio, r.maior_atraso, r.abertos_vencidos, r.bloqueado ? "sim" : "nao"]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";"));
    const blob = new Blob(["﻿" + [head.join(";"), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `score-clientes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const maxPct = 100;

  return (
    <div className="space-y-6" data-testid="score-page">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-white">Score de Clientes</h1>
          <p className="text-slate-400 text-sm mt-1 max-w-2xl">
            Nota de 0 a 1000 sobre como cada cliente paga. Boleto que vence em sábado, domingo ou feriado e é pago no
            próximo dia útil <b className="text-slate-200">não conta como atraso</b>.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={months} onChange={(e) => setMonths(Number(e.target.value))} data-testid="score-months"
            className="bg-slate-950 border border-slate-800 text-slate-200 text-sm rounded-md h-9 px-2">
            <option value={6}>Últimos 6 meses</option>
            <option value={12}>Últimos 12 meses</option>
            <option value={24}>Últimos 24 meses</option>
          </select>
          <select value={tolerance} onChange={(e) => setTolerance(Number(e.target.value))} data-testid="score-tolerance"
            className="bg-slate-950 border border-slate-800 text-slate-200 text-sm rounded-md h-9 px-2">
            <option value={0}>Sem tolerância</option>
            <option value={1}>Tolerância 1 dia</option>
            <option value={2}>Tolerância 2 dias</option>
            <option value={3}>Tolerância 3 dias</option>
            <option value={5}>Tolerância 5 dias</option>
          </select>
          <Button variant="outline" className="border-slate-700 text-slate-200" onClick={() => load(true)} disabled={loading} data-testid="score-refresh">
            <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} />Atualizar
          </Button>
          <Button variant="outline" className="border-slate-700 text-slate-200" onClick={exportCsv} disabled={!rows.length} data-testid="score-export">
            <Download size={14} className="mr-1" />CSV
          </Button>
        </div>
      </div>

      {sm && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            <Kpi testid="kpi-media" label="Score médio" value={sm.media} hint={`${sm.clientes} clientes ativos`} />
            <Kpi testid="kpi-pont" label="Pontualidade geral" value={`${sm.pontualidade_geral}%`} hint="boletos pagos sem atraso" />
            <Kpi testid="kpi-open" label="Atraso em aberto" value={sm.com_atraso_aberto} hint="clientes com boleto vencido" />
            <Kpi testid="kpi-du" label="Dia útil seguinte" value={sm.pagaram_proximo_dia_util} hint="pagos sem juros pela regra" />
            <Kpi testid="kpi-up" label="Melhorando" value={sm.melhorando} hint="últimos 90 dias" />
            <Kpi testid="kpi-down" label="Piorando" value={sm.piorando} hint="últimos 90 dias" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="bg-slate-900/50 border-slate-800 p-4">
              <div className="text-slate-300 text-sm font-medium mb-3">Distribuição por faixa <span className="text-slate-500 font-normal">(clique para filtrar)</span></div>
              <div className="space-y-2">
                {Object.keys(BANDS).map((k) => {
                  const n = sm.distribuicao[k] || 0;
                  const pct = sm.clientes ? (100 * n) / sm.clientes : 0;
                  return (
                    <button key={k} type="button" onClick={() => setBand(band === k ? "" : k)} data-testid={`band-${k}`}
                      className={`w-full flex items-center gap-3 text-left rounded-md p-1 ${band === k ? "ring-1 ring-emerald-500/60" : ""}`}>
                      <span className="w-40 text-xs text-slate-300">{k} · {BANDS[k].label} <span className="text-slate-600">({BANDS[k].range})</span></span>
                      <div className="flex-1 h-3 rounded-full bg-slate-800 overflow-hidden"><div className={`h-full ${BANDS[k].bar}`} style={{ width: `${pct}%` }} /></div>
                      <span className="w-16 text-right text-sm text-white tabular-nums">{n} <span className="text-slate-500 text-xs">({Math.round(pct)}%)</span></span>
                    </button>
                  );
                })}
              </div>
            </Card>
            <Card className="bg-slate-900/50 border-slate-800 p-4">
              <div className="text-slate-300 text-sm font-medium mb-3">Pagamentos em dia por mês de vencimento</div>
              <div className="flex items-end gap-2 h-32">
                {(data.evolucao || []).map((m) => (
                  <div key={m.mes} className="flex-1 flex flex-col items-center justify-end h-full" title={`${m.mes}: ${m.em_dia} de ${m.total} em dia`}>
                    <span className="text-[10px] text-slate-400 mb-1">{Math.round(m.pct)}%</span>
                    <div className="w-full rounded-t bg-emerald-500/80" style={{ height: `${(m.pct / maxPct) * 85}%` }} />
                    <span className="text-[10px] text-slate-500 mt-1">{m.mes.slice(5)}/{m.mes.slice(2, 4)}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </>
      )}

      <Card className="bg-slate-900/50 border-slate-800 p-0 overflow-hidden">
        <div className="p-4 flex flex-wrap gap-2 items-center border-b border-slate-800">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nome, login ou telefone"
              data-testid="score-search"
              className="pl-9 bg-slate-950 border-slate-800 text-white w-72 focus-visible:ring-emerald-500/40" />
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="score-sort"
            className="bg-slate-950 border border-slate-800 text-slate-200 text-sm rounded-md h-9 px-2">
            {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          {band && <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => setBand("")}>Faixa {band} ✕</Button>}
          <div className="w-full flex flex-wrap gap-2 pt-1">
            {VIEWS.map((v) => (
              <button key={v.id} type="button" onClick={() => setView(v.id)} data-testid={`view-${v.id}`}
                className={`px-3 py-1 rounded-full text-xs border ${view === v.id ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}>
                {v.label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="score-table">
            <thead className="bg-slate-900 text-slate-400 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left p-3">#</th>
                <th className="text-left p-3">Cliente</th>
                <th className="text-left p-3">Score</th>
                <th className="text-left p-3">Faixa</th>
                <th className="text-left p-3">Perfil</th>
                <th className="text-right p-3">Pontual.</th>
                <th className="text-right p-3">Atraso médio</th>
                <th className="text-right p-3">Em aberto</th>
                <th className="text-left p-3">Tendência</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data && <tr><td colSpan={9} className="p-6 text-center text-slate-500">Calculando…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-500">Nenhum cliente nesse filtro.</td></tr>}
              {slice.map((r, i) => (
                <tr key={r.login} onClick={() => setDetail(r.login)} data-testid={`score-row-${r.login}`}
                  className="border-t border-slate-800 hover:bg-slate-800/40 cursor-pointer">
                  <td className="p-3 text-slate-500">{(page - 1) * PAGE + i + 1}</td>
                  <td className="p-3">
                    <div className="text-white">{r.nome}</div>
                    <div className="text-xs text-slate-500">{r.login}{r.bloqueado ? " · bloqueado" : ""}</div>
                  </td>
                  <td className="p-3"><ScoreBadge row={r} /></td>
                  <td className="p-3"><BandPill band={r.band} /></td>
                  <td className="p-3 text-slate-300">{r.perfil}</td>
                  <td className="p-3 text-right tabular-nums">{r.pontualidade == null ? "—" : `${r.pontualidade}%`}</td>
                  <td className="p-3 text-right tabular-nums">{r.atraso_medio ? `${r.atraso_medio} d` : "—"}</td>
                  <td className={`p-3 text-right tabular-nums ${r.abertos_vencidos ? "text-red-300" : "text-slate-500"}`}>{r.abertos_vencidos || "—"}</td>
                  <td className="p-3"><Trend t={r.tendencia} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="p-3 flex items-center justify-between text-xs text-slate-500 border-t border-slate-800">
          <span>{rows.length} cliente(s){data?.generated_at ? ` · calculado às ${new Date(data.generated_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : ""}</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 h-7" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
            <span>{page} / {pages}</span>
            <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 h-7" disabled={page >= pages} onClick={() => setPage(page + 1)}>Próxima</Button>
          </div>
        </div>
      </Card>

      <Card className="bg-slate-900/50 border-slate-800 p-4">
        <button type="button" className="flex items-center gap-2 text-slate-300 text-sm" onClick={() => setShowHow(!showHow)} data-testid="score-how">
          <Info size={16} />Como o score é calculado
        </button>
        {showHow && (
          <div className="mt-3 text-sm text-slate-400 space-y-2">
            <p><b className="text-slate-200">Atraso</b> é contado em dias corridos a partir do <b>vencimento efetivo</b>: se o boleto vence em sábado, domingo ou feriado
              nacional (inclui Carnaval, Sexta-feira Santa e Corpus Christi), vale o próximo dia útil. Pagar até esse dia não é atraso. Boleto em aberto e já vencido conta como atraso até ser pago.</p>
            <p><b className="text-slate-200">Quatro componentes:</b> pontualidade (40%), gravidade dos atrasos (25%), últimos 90 dias (20%) e sequência de boletos em dia (15%).
              Boletos recentes pesam mais (meia-vida de 6 meses), então quem melhorou sobe e quem piorou desce.</p>
            <p><b className="text-slate-200">Atraso em aberto hoje</b> desconta pontos (de 50 a 300, conforme os dias e a quantidade de boletos).
              Quem atrasa em mais de 40% dos boletos não passa da faixa C, e quem atrasa em mais de 70% não passa da faixa E.</p>
            <p><b className="text-slate-200">Poucos boletos</b> (menos de 3): o cliente é marcado como Novo e a nota é puxada para o neutro, para não julgar por pouco dado.
              Pagar adiantado não dá pontos extras; aparece só como informação.</p>
            <p><b className="text-slate-200">Faixas:</b> A 800–1000 · B 650–799 · C 500–649 · D 350–499 · E 0–349. Considera só mensalidades de clientes ativos.
              A tolerância (opcional) aceita alguns dias de atraso sem penalizar.</p>
          </div>
        )}
      </Card>

      <DetailDialog login={detail} params={{ months, tolerance }} onClose={() => setDetail(null)}
        onTitles={(l) => { setTitlesLogin(l); }} />
      {titlesLogin && <ClientTitlesDialog login={titlesLogin} onClose={() => setTitlesLogin(null)} />}
    </div>
  );
}
